/* Words the access-policy screen builds from data: resource names, rules as sentences, and ages.
 *
 * Kept apart from the components because three panels say the same things — the editor, the
 * versions history and the request checker all render a rule as a sentence — and three spellings of
 * "reader may read every dataset" would drift into three readings of one rule.
 */

import type { TFunction } from 'i18next';

import type { PolicyRule } from '@/shared/api/generated/policy';

/** A resource type's plural label, for a picker and a summary heading. Falls back to the raw type,
 * which is at least unambiguous, for a type this build has no words for yet. */
export function resourceLabel(t: TFunction, resourceType: string): string {
  return t(`policy.resource.${resourceType}.label`, { defaultValue: resourceType });
}

/** One resource of a type, for "every dataset" and "one dataset". */
export function resourceOne(t: TFunction, resourceType: string): string {
  const fallback = (resourceType.split('.').pop() ?? resourceType).replaceAll('_', ' ');
  return t(`policy.resource.${resourceType}.one`, { defaultValue: fallback });
}

/** How a specific resource is named: a dataset by its qualified name, and never by a raw id.
 *
 * An id the catalogue listing does not name — a dataset since removed, or one past the listing's
 * bound — reads as "unlisted", with the id left for a tooltip, rather than putting a UUID in a
 * sentence somebody has to read. */
export function resourceName(
  t: TFunction,
  names: ReadonlyMap<string, string> | undefined,
  resourceId: string,
): string {
  return names?.get(resourceId) ?? t('policy.resource.unlisted');
}

/** A rule as the sentence it means: "reader may read every dataset". */
export function ruleSentence(
  t: TFunction,
  rule: PolicyRule,
  names: ReadonlyMap<string, string> | undefined,
): string {
  const one = resourceOne(t, rule.resource_type);
  const target =
    rule.resource_id === null
      ? t('policy.rule.every', { one })
      : t('policy.rule.oneNamed', {
          one,
          name: resourceName(t, names, rule.resource_id),
        });
  return t(rule.effect === 'deny' ? 'policy.rule.mayNot' : 'policy.rule.may', {
    role: rule.role,
    action: rule.action,
    target,
  });
}
